import { lazy, Suspense, type ReactNode } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { LogoIcon } from "@/components/branding/LogoIcon";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { AnnouncementsPage } from "@/pages/AnnouncementsPage";
import { DevelopmentPage } from "@/pages/DevelopmentPage";
import { HomePage } from "@/pages/HomePage";
import { MorePage } from "@/pages/MorePage";
import { TimetablePage } from "@/pages/TimetablePage";

const AboutPage = lazy(() =>
  import("@/pages/AboutPage").then((m) => ({ default: m.AboutPage })),
);
const CommunityPage = lazy(() =>
  import("@/pages/CommunityPage").then((m) => ({ default: m.CommunityPage })),
);
const CommitteePage = lazy(() =>
  import("@/pages/CommitteePage").then((m) => ({ default: m.CommitteePage })),
);
const ContributePage = lazy(() =>
  import("@/pages/ContributePage").then((m) => ({ default: m.ContributePage })),
);
const NotFoundPage = lazy(() =>
  import("@/pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })),
);
const DisplayPage = lazy(() =>
  import("@/pages/DisplayPage").then((m) => ({ default: m.DisplayPage })),
);
const AdminRouter = lazy(() =>
  import("@/pages/admin/AdminRouter").then((m) => ({ default: m.AdminRouter })),
);

function Loading() {
  return (
    <div className="grid min-h-[40vh] place-items-center text-muted" aria-live="polite">
      Loading…
    </div>
  );
}

function FullPageLoading() {
  return (
    <div
      className="grid min-h-dvh place-items-center bg-paper px-4 text-muted"
      aria-live="polite"
    >
      <div className="flex flex-col items-center gap-3">
        <LogoIcon className="h-16 w-16" title="Jama Masjid Dariyapur" />
        <span className="text-sm">Loading…</span>
      </div>
    </div>
  );
}

const susp = (child: ReactNode) => <Suspense fallback={<Loading />}>{child}</Suspense>;

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route index element={<HomePage />} />
          <Route path="timetable" element={<TimetablePage />} />
          <Route path="announcements" element={<AnnouncementsPage />} />
          <Route path="development" element={<DevelopmentPage />} />
          <Route path="more" element={<MorePage />} />
          <Route path="about" element={susp(<AboutPage />)} />
          <Route path="community" element={susp(<CommunityPage />)} />
          <Route path="committee" element={susp(<CommitteePage />)} />
          <Route path="contribute" element={susp(<ContributePage />)} />
        </Route>
        <Route
          path="display"
          element={
            <Suspense fallback={<FullPageLoading />}>
              <DisplayPage />
            </Suspense>
          }
        />
        <Route
          path="admin/*"
          element={
            <Suspense fallback={<FullPageLoading />}>
              <AdminRouter />
            </Suspense>
          }
        />
        <Route
          path="*"
          element={
            <Suspense fallback={<FullPageLoading />}>
              <NotFoundPage />
            </Suspense>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
